import { useCallback, useEffect, useState } from 'react'

export type PreferenciaTema = 'claro' | 'escuro' | 'sistema'

const CHAVE = 'baiocchi:tema'

function lerPreferencia(): PreferenciaTema {
  try {
    const salvo = localStorage.getItem(CHAVE)
    if (salvo === 'claro' || salvo === 'escuro') return salvo
  } catch {
    /* localStorage bloqueado (aba privada, política): segue no padrão. */
  }
  return 'sistema'
}

function aplicar(preferencia: PreferenciaTema): void {
  const escuro =
    preferencia === 'escuro' ||
    (preferencia === 'sistema' && window.matchMedia('(prefers-color-scheme: dark)').matches)
  document.documentElement.classList.toggle('tema-escuro', escuro)
}

/**
 * Tema claro/escuro.
 *
 * Padrão "sistema": o CRM fica aberto o dia inteiro e a escolha do sistema
 * operacional já reflete a iluminação da mesa. O `index.html` aplica a classe
 * antes da primeira pintura, para não haver flash branco na recarga.
 */
export function useTema(): {
  preferencia: PreferenciaTema
  escuro: boolean
  definir: (preferencia: PreferenciaTema) => void
  alternar: () => void
} {
  const [preferencia, setPreferencia] = useState<PreferenciaTema>(lerPreferencia)
  const [escuro, setEscuro] = useState(() =>
    typeof document === 'undefined'
      ? false
      : document.documentElement.classList.contains('tema-escuro'),
  )

  useEffect(() => {
    aplicar(preferencia)
    setEscuro(document.documentElement.classList.contains('tema-escuro'))

    if (preferencia !== 'sistema') return

    const consulta = window.matchMedia('(prefers-color-scheme: dark)')
    const aoMudar = () => {
      aplicar('sistema')
      setEscuro(consulta.matches)
    }
    consulta.addEventListener('change', aoMudar)
    return () => consulta.removeEventListener('change', aoMudar)
  }, [preferencia])

  const definir = useCallback((nova: PreferenciaTema) => {
    setPreferencia(nova)
    try {
      if (nova === 'sistema') localStorage.removeItem(CHAVE)
      else localStorage.setItem(CHAVE, nova)
    } catch {
      /* preferência vale só nesta sessão */
    }
  }, [])

  const alternar = useCallback(() => {
    definir(document.documentElement.classList.contains('tema-escuro') ? 'claro' : 'escuro')
  }, [definir])

  return { preferencia, escuro, definir, alternar }
}
